import { useCallback } from "react";

import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { getPhonesEmails } from "Components/shared/SearchableDialPad/ContactSearch/queries";

import { SpotlightInAppActionItem } from "./SpotlightInAppActionItem";

export const useGetCallCustomers = (onCallback: () => void) => {
    const [getPhonesGQL, { loading }] = useLazyQuery(getPhonesEmails);
    const navigate = useNavigate();

    const getCallCustomers = useCallback(
        async (value: string, targetAction?: "call" | "email") => {
            if (!value.length) {
                return [];
            }

            const response = await getPhonesGQL({
                variables: {
                    queryParams: {
                        filterBy: [
                            {
                                key: "name",
                                value: value,
                                operator: "starts_with",
                            },
                            {
                                key: "email",
                                value: value,
                                operator: "starts_with",
                            },
                        ],
                    },
                },
            });

            // remove duplicate items with same id
            const uniqueItems = response.data.getPhonesEmails.items.filter(
                (item: any, index: number, self: any) => index === self.findIndex((t: any) => t.id === item.id)
            );

            if (uniqueItems.length === 0) {
                return [];
            }

            const options = uniqueItems
                .filter((item: any) => item.contactPhone)
                .map((item: any) => {
                    const hasName = item.firstName || item.lastName;
                    return {
                        label: (
                            <SpotlightInAppActionItem
                                name={hasName ? `${item.firstName} ${item.lastName}` : item.email}
                                description={item.contactPhone}
                                searchValue={value}
                                icon={
                                    targetAction == "call" ? (
                                        <RVIcon size={RVIconSizeOptions.XSMALL} icon="place-call" />
                                    ) : (
                                        <RVIcon size={RVIconSizeOptions.XSMALL} icon="email" />
                                    )
                                }
                                onCallback={() => {
                                    if (targetAction == "call") {
                                        if (onCallback) {
                                            onCallback();
                                        }
                                        return navigate(`/agent?phoneNumber=${item.contactPhone}&contactId=${item.id}`);
                                    }
                                }}
                            />
                        ),
                        value: item.route,
                    };
                });

            return [
                {
                    label: "Customers",
                    options,
                },
            ];
        },
        [getPhonesGQL, navigate]
    );

    return { getCallCustomers, loading };
};
